/*
# -----------------------------------------------------------------------------
#	Project: HSE Genius®
#	Client ID: 834215
#	Registration Code: IT-2025-ZR319-MQ744
#	Author: KaimakiWeb
#	Website: https://kaimakiweb.com
#	Copyright (c) 2024-2026 Kaimaki Web. All rights reserved.
#
#	This software and its source code are the property of KaimakiWeb.
#	Unauthorized copying, modification, distribution, or use of this software,
#	via any medium, is strictly prohibited unless prior written permission
#	is obtained from KaimakiWeb.
# -----------------------------------------------------------------------------
*/

import ApiService from "../api-service";
import { AuthStorage } from "../../../services/storage/auth.storage";
import { isInviteAcceptanceUrl } from "../../../utils/invitation";

// Helper function to check if current URL is an invite acceptance URL
const _isInviteAcceptanceUrl = isInviteAcceptanceUrl;

class AuthApiService extends ApiService {
  signIn = async (data) =>
    await this.post("/token", data).then((res) => res.data);

  logout = async () => {
    // The refresh token is in the httpOnly cookie — the backend reads it from there.
    // withCredentials is set globally by the interceptor so the cookie is included.
    return await this.post("/logout", {}).then((res) => res.data);
  };

  getAuthUser = async () => {
    // If we're on an invite acceptance page, return a mock profile to prevent 401 errors
    if (_isInviteAcceptanceUrl()) {
      return {
        id: "temp-user",
        email: "temp@example.com",
        first_name: "Invite",
        last_name: "User",
        is_temporary: true,
      };
    }
    // Profile endpoint requires a CompanyUserRole; callers must check
    // onboarding-status first to avoid a hard 403 for newly-registered users.
    return await this.get("/profile").then((res) => res.data);
  };

  // Lightweight check used right after login (and on app bootstrap) to decide
  // whether the user can enter the app or must first complete onboarding.
  getOnboardingStatus = async () =>
    await this.get("/onboarding-status").then((res) => res.data);

  updateProfile = async (data) =>
    await this.patch("/profile", data).then((res) => res.data);

  // MFA — all three calls pass pre_auth_token in the body (not the Authorization header).

  setupMfa = async (preAuthToken) =>
    await this.get(`/mfa/setup`, {pre_auth_token:preAuthToken}).then(
      (res) => res.data
    );

  verifyMfa = async ({ preAuthToken, code }) =>
    await this.post("/mfa/verify", { pre_auth_token: preAuthToken, code }).then(
      (res) => res.data
    );

  confirmMfa = async ({ preAuthToken, code }) =>
    await this.post("/mfa/confirm", { pre_auth_token: preAuthToken, code }).then(
      (res) => res.data
    );
}

export const AuthApi = new AuthApiService("user");
